import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { ContributorPerson, useAuth } from "@/context/Auth/CombinedAuthContext";
import { createContributorInvoice } from "@/services/external-contributors-functions";
import { CreateContributorInvoiceParams } from "@/types/external-contributors.types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { FiPlusCircle } from "react-icons/fi";
import { MdDelete } from "react-icons/md";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "sonner";
import { z } from "zod";

const rowSchema = z.object({
	amount: z.number().min(1, {
		message: "Amount is required",
	}),
	description: z.string().min(1, {
		message: "Description is required",
	}),
	itemquantity: z.number().min(1, {
		message: "Item quantity is required",
	}),
});

const formSchema = z.object({
	rows: z.array(rowSchema).min(1),
});

const CreateInvoice = () => {
	const user = useAuth().person as ContributorPerson;
	const [open, setOpen] = useState(false);

	const queryClient = useQueryClient();

	const createInvoice = useMutation({
		mutationKey: "create_contributor_invoice",
		mutationFn: (data: CreateContributorInvoiceParams[]) =>
			createContributorInvoice(user.accessToken, data),
		onSuccess: () => {
			success();
		},
		onError: () => {
			error();
		},
		retry: false,
	});

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			rows: [
				{
					amount: 0,
					description: "",
					itemquantity: 0,
				},
			],
		},
	});

	const fieldArray = useFieldArray({
		control: form.control,
		name: "rows",
	});

	const rows = useWatch({
		name: "rows",
		control: form.control,
	});

	const onSubmit = (data: z.infer<typeof formSchema>) => {
		console.log(data);
		createInvoice.mutate(data.rows);
	};

	const disableFields = () => {
		if (createInvoice.isLoading) return true;
		if (createInvoice.isSuccess) return true;
		return false;
	};

	const success = () => {
		toast.success("Claim Created");
		queryClient.invalidateQueries(["invoice"]);
		createInvoice.reset();
		form.reset();
		setOpen(false);
	};

	const error = () => {
		toast.error("Error creating invoice, try again");
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>
				<Button>Create Invoice</Button>
			</DialogTrigger>
			<DialogContent className="max-w-4xl">
				<div className="overflow-x-auto px-1">
					<DialogHeader>
						<DialogTitle className="text-left">Create Invoice</DialogTitle>
					</DialogHeader>
					<>
						<Form {...form}>
							<form onSubmit={form.handleSubmit(onSubmit)}>
								<Table className="mt-4 table-fixed">
									<TableHeader className="">
										<TableRow>
											<TableHead className="w-4">#</TableHead>
											<TableHead className="min-w-8">Description</TableHead>
											<TableHead className="text-center w-36">Amount</TableHead>
											<TableHead className="text-center w-36">Item Quantity</TableHead>
											<TableHead className="w-36">Action</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{fieldArray.fields.map((fd, index) => (
											<TableRow key={fd.id}>
												<TableCell>{index + 1}</TableCell>
												<TableCell>
													<FormField
														name={`rows.${index}.description`}
														render={({ field }) => (
															<FormItem className="mb-2">
																<FormControl>
																	<Input {...field} disabled={disableFields()} />
																</FormControl>
																<FormMessage className="font-light" />
															</FormItem>
														)}
													/>
												</TableCell>
												<TableCell>
													<FormField
														name={`rows.${index}.amount`}
														render={({ field }) => (
															<FormItem>
																<FormControl>
																	<Input
																		type="number"
																		placeholder="Amount"
																		className="mt-0"
																		onChange={(e) => field.onChange(e.target.valueAsNumber)}
																		min={0}
																		disabled={disableFields()}
																	/>
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>
												</TableCell>
												<TableCell>
													<FormField
														name={`rows.${index}.itemquantity`}
														render={({ field }) => (
															<FormItem>
																<FormControl>
																	<Input
																		type="number"
																		className="mt-0 min-w-[100px]"
																		onChange={(e) => field.onChange(e.target.valueAsNumber)}
																	/>
																</FormControl>
																<FormMessage />
															</FormItem>
														)}
													/>
												</TableCell>
												<TableCell>
													<Button
														className="bg-red-600 hover:bg-red-500"
														onClick={() => fieldArray.remove(index)}
														disabled={disableFields()}
													>
														<MdDelete className="text-xl" />
													</Button>
												</TableCell>
											</TableRow>
										))}
										<TableRow>
											<TableCell>
												<Button
													onClick={() =>
														fieldArray.append({
															amount: -1,
															description: "",
															itemquantity: -1,
														})
													}
													type="button"
													disabled={disableFields()}
												>
													<FiPlusCircle className="mr-2 text-lg" />
													Add New Row
												</Button>
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
								{form.formState.errors.rows?.root?.message && (
									<FormMessage>{form.formState.errors.rows.root.message}</FormMessage>
								)}
								<Button
									type="submit"
									className="mt-4 w-full"
									disabled={disableFields()}
								>
									{disableFields() ? "Creating Invoice..." : "Submit"}
								</Button>
							</form>
						</Form>
					</>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default CreateInvoice;
